import React from "react";
import { FormHelperText, TextField, Typography } from "@mui/material";
import DocumentUpload from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { useNotification } from "@context/NotificationContext";
import { useTranslation } from "react-i18next";
import { MAX_TEXT_LENGTH, rejectLicenseSchema } from "./rejectLicenseSchema";

interface RejectLicenseDrawerProps {
  isOpen: boolean;
  onClose: (drawerName: string) => void;
  onSubmit?: (payload: { rejectionNotes: string }) => void;
  refOrderNo: number;
  isPortOperatorContext?: boolean;
  onDocumentUpload?: (file: File) => void;
  isUploading?: boolean;
  isSubmitting?: boolean;
}

const EMPTY: UploadEntry[] = [];

const RejectLicenseDrawer: React.FC<RejectLicenseDrawerProps> = ({
  isOpen,
  onClose,
  onSubmit,
  refOrderNo,
  isPortOperatorContext = false,
  onDocumentUpload,
  isUploading,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  const { notify } = useNotification();
  const [notes, setNotes] = React.useState("");
  const [error, setError] = React.useState<string | undefined>(undefined);

  const isPrintableKey = (e: React.KeyboardEvent) =>
    e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

  const rejectionOnlySchema = rejectLicenseSchema(t).pick({ rejectionNotes: true });

  const rejectionFiles = useUploadStore((s) => s.filesByKey.licenseRejectionAttachment ?? EMPTY);
  const isRejectionUploadInFlight = useUploadStore((s) =>
    s.isUploadInFlight("licenseRejectionAttachment"),
  );
  const setFilesForKey = useUploadStore((s) => s.setFilesForKey);
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);

  const fileProfiles = useFileUploadProfiles();
  const rejectionProfile = fileProfiles.sideDrawerApprovalFile;

  const allowedExtensions = rejectionProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const reset = React.useCallback(() => {
    setNotes("");
    setError(undefined);
    clearFilesForKey("licenseRejectionAttachment");
  }, [clearFilesForKey]);

  const prevOpenRef = React.useRef(isOpen);
  React.useEffect(() => {
    if (prevOpenRef.current && !isOpen) {
      reset();
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, reset]);

  const handleClose = () => {
    onClose("rejectDrawer");
    reset();
  };

  const handleCancel = () => {
    onClose("rejectDrawer");
    reset();
  };

  const handleUpload = (newFiles: File[]) => {
    if (newFiles.length > 1) return;
    const file = newFiles[0];
    if (!file) return;

    setFilesForKey("licenseRejectionAttachment", [file]);
    onDocumentUpload?.(file);
  };

  const handleSubmit = () => {
    if (isUploading || isRejectionUploadInFlight) {
      notify({
        title: t("common.notification.please-wait-for-upload"),
        type: "warning",
      });
      return;
    }

    const validation = rejectLicenseSchema(t).safeParse({ rejectionNotes: notes });
    if (!validation.success) {
      const flat = validation.error.flatten().fieldErrors;
      setError(flat.rejectionNotes?.[0]);
      return;
    }

    onSubmit?.({ rejectionNotes: validation.data.rejectionNotes });
  };

  if (!isOpen) return null;

  return (
    <div data-testid="reject-license-drawer-form" className="min-w-[400px]">
      <SideDrawer
        isOpen={isOpen}
        title={
          isPortOperatorContext
            ? t("licensing.port-operator-license-reject-title")
            : t("licensing.vendor-license-reject-title")
        }
        onClose={handleClose}
        onCancel={handleCancel}
        submitLabel={t("licensing.reject-button")}
        onSubmit={handleSubmit}
        isSubmitting={Boolean(isSubmitting)}
      >
        <Typography sx={{ px: 3, pb: 2 }} data-testid="reject-license-subtitle">
          {isPortOperatorContext
            ? t("licensing.port-operator-license-reject-subtitle")
            : t("licensing.vendor-license-reject-subtitle")}
          <br />
          <span style={{ fontWeight: 600 }}>{refOrderNo}.</span>
        </Typography>
        <div className="flex-1 w-[400px] py-2">
          <div className="flex flex-col gap-2 px-6">
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {t("licensing.notes")}
            </Typography>

            <TextField
              multiline
              minRows={4}
              value={notes}
              error={Boolean(error)}
              slotProps={{ htmlInput: { maxLength: MAX_TEXT_LENGTH } }}
              sx={{ "& textarea": { padding: "12px", borderRadius: "8px" } }}
              onKeyDown={(e) => {
                if (!isPrintableKey(e)) return;
                if (notes.length >= MAX_TEXT_LENGTH) {
                  setError(
                    t("licensing.form-validation.max-length", {
                      label: t("licensing.notes"),
                      max: MAX_TEXT_LENGTH,
                    }),
                  );
                }
              }}
              onBlur={() => {
                const res = rejectionOnlySchema.safeParse({ rejectionNotes: notes });
                if (!res.success) {
                  const flat = res.error.flatten().fieldErrors;
                  setError(flat.rejectionNotes?.[0]);
                } else {
                  setError(undefined);
                }
              }}
              onChange={(e) => {
                const val = e.target.value;
                setNotes(val);

                const res = rejectionOnlySchema.safeParse({ rejectionNotes: val });
                if (!res.success) {
                  const flat = res.error.flatten().fieldErrors;
                  setError(flat.rejectionNotes?.[0]);
                } else {
                  setError(undefined);
                }
              }}
            />

            {error && <FormHelperText sx={{ color: "error.main", m: 0 }}>{error}</FormHelperText>}

            <FormHelperText
              sx={{
                ml: 0,
                mt: 0.5,
                fontSize: 12,
                textAlign: "right",
                color: "text.secondary",
              }}
            >
              {notes.length}/{MAX_TEXT_LENGTH}
            </FormHelperText>

            <DocumentUpload
              label={t("licensing.attachments")}
              testId="license-reject-attachment-upload"
              name="licenseRejectionAttachment"
              maxFiles={1}
              allowedExtensions={allowedExtensions}
              onUpload={handleUpload}
              conditions={rejectionProfile}
              files={rejectionFiles}
              setFiles={(next) => setFilesForKey("licenseRejectionAttachment", next)}
              uploadDescription={t("upload.dragOrDropDescription")}
            />

            {rejectionFiles[0] && (
              <SingleFile
                file={rejectionFiles[0]}
                fieldName="licenseRejectionAttachment"
                setFiles={() => clearFilesForKey("licenseRejectionAttachment")}
                removeFn={() => clearFilesForKey("licenseRejectionAttachment")}
                isLoading={Boolean(isUploading || isRejectionUploadInFlight)}
              />
            )}
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default RejectLicenseDrawer;
